import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TitleBar from '../TitleBar';
import BottomNavigation from '../BottomNavigation';
import ConnectedPolicies from 'screens/Mutations/templates/ConnectedPolicies';
import { LoanFormType } from 'screens/Mutations/utils/types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { yupResolver } from '@hookform/resolvers/yup';
import { saveLoan } from 'store/ohaFormReducer';
import { getPoliciesFormSchema } from '../../../../schemas/policies/policiesSchema';
import { useOhaFormStep } from 'screens/Mutations/hooks/useOhaFormStep';
import { mapOHAForm } from 'screens/Mutations/utils/mappers/oha/mapFormToApi';
import FormTemplate from 'components/Templates/FormTemplate';
import FormStepper from '../FormStepper';
import { Form } from 'components/Molecules/components/Form/Form.styled';
import CustomSnackbar from 'components/Snackbar/CustomSnackbar';

const ConnectedPoliciesForm: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState<{ status: string; name: string }>();
  const ohaFormState = useSelector((state: RootState) => state.ohaForm);

  const formMethods = useForm<LoanFormType>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      ...ohaFormState.loan,
      currentLoanParts: ohaFormState.loan.currentLoanParts?.map((loanPart) => ({
        ...loanPart,
        convertLoan: { ...loanPart.convertLoan },
        splitLoan: loanPart.splitLoan?.map((splitLoan) => ({ ...splitLoan })),
        policies: loanPart.policies?.map((policy) => ({
          ...policy,
          policyHolders: { ...policy.policyHolders },
          insuredPersons: { ...policy.insuredPersons },
        })),
      })),
      policies: ohaFormState.loan.policies.map((policy) => ({
        ...policy,
        policyHolders: { ...policy.policyHolders },
        insuredPersons: { ...policy.insuredPersons },
      })),
    },
    resolver: yupResolver(getPoliciesFormSchema()),
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
  } = formMethods;

  const [
    amount,
    loanNumber,
    extraPayOffAmount,
    changeCurrentMortgage,
    changeLifeInsurancePolicy,
    currentLoanParts,
    newLoanParts,
    policies,
    amountRaise,
  ] = watch([
    'amount',
    'loanNumber',
    'extraPayOffAmount',
    'changeCurrentMortgage',
    'changeLifeInsurancePolicy',
    'currentLoanParts',
    'newLoanParts',
    'policies',
    'amountRaise',
  ]);

  const loanValues: LoanFormType = useMemo(
    () => ({
      amount,
      loanNumber,
      extraPayOffAmount,
      changeCurrentMortgage,
      changeLifeInsurancePolicy,
      currentLoanParts,
      newLoanParts,
      policies,
      amountRaise,
    }),
    [
      amount,
      loanNumber,
      extraPayOffAmount,
      changeCurrentMortgage,
      changeLifeInsurancePolicy,
      currentLoanParts,
      newLoanParts,
      policies,
      amountRaise,
    ]
  );

  const completeForm = {
    ...ohaFormState,
    loan: loanValues,
  };

  const onSaveError = () => handleNotification('error', 'submit');

  const { save, isSaving, goToNextStep } = useOhaFormStep({ onSaveError });

  const onSubmit = async () => {
    dispatch(saveLoan(loanValues));

    if (isDirty) {
      await save(mapOHAForm(completeForm));
    }
    goToNextStep();
  };

  const dispatchForm = useCallback(() => {
    dispatch(saveLoan(loanValues));
  }, [loanValues, dispatch]);

  const handleNotification = useCallback(
    (status: string, name: string) => setSnackbar({ status, name }),
    []
  );

  const handleOnClose = useCallback(() => setSnackbar(undefined), []);

  return (
    <>
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormTemplate
            header={
              <TitleBar
                onResult={handleNotification}
                formState={completeForm}
                resetForm={reset}
                isFormDirty={isDirty}
                dispatchCurrentForm={dispatchForm}
              />
            }
            sidebar={<FormStepper />}
            footer={
              <BottomNavigation dispatchCurrentForm={dispatchForm} isLoadingSubmit={isSaving} />
            }
          >
            <ConnectedPolicies loanForm={loanValues} />
          </FormTemplate>
        </Form>
      </FormProvider>
      {snackbar && (
        <CustomSnackbar
          isOpen={!!snackbar}
          handleClose={handleOnClose}
          severity={snackbar.status === 'success' ? 'success' : 'error'}
          message={t(`directMutations.snackbar.${snackbar.name}.${snackbar.status}`)}
        />
      )}
    </>
  );
};

export default ConnectedPoliciesForm;
